// Excel + PDF exports of the Full Ledger, built from exactly what the screen is
// showing — the rows left after every filter and the search box, in the order
// they appear.
//
// Distinct from LedgerDownloadDialog, which re-queries the server with its own
// independent filter UI. This one never re-fetches: what you see is what you
// get, which is the whole point of exporting from the filter panel.
//
// Both formats open with the same block spelling out which filters were in
// force, so a sheet or print-out that gets passed around can still be read on
// its own terms — "which vehicle was this, and over what dates?".

import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

export interface LedgerExportOptions {
  /** The filtered, sorted rows currently on screen. */
  rows: any[];
  /** Which extra Subledger slots are revealed, so the export matches the table. */
  activeSlots: number[];
  /**
   * The ledger hierarchy that was filtered, as one path —
   * "Transport / Fuel / Four Wheeler". Reads the way the client says it out
   * loud, rather than as one labelled line per level.
   */
  ledgerPath: string;
  /** [label, value] pairs for the filters that sit outside that hierarchy. */
  summary: [string, string][];
  /** e.g. "August 2026" — the heading's subtitle. */
  periodLabel: string;
  /**
   * Base file name, before the extension and before punctuation is stripped —
   * e.g. "Filtered-Ledger_05 Aug 2026 to 14 Aug 2026". Separate from
   * periodLabel so the name can carry the day range while the heading keeps
   * naming the month.
   */
  fileBase: string;
  /** Credits minus debits over `rows`, matching the on-screen subtotal. */
  netTotal: number;
}

interface Col {
  label: string;
  /** Excel column width, in characters. */
  width: number;
  /** Relative weight used to share out the PDF's usable width. */
  weight: number;
  numeric?: boolean;
  /**
   * Whether the PDF carries this column too. The Excel takes every field the
   * ledger holds — it scrolls, and the point of exporting to a sheet is to have
   * the lot. The PDF is a fixed-width printed page, so it keeps the subset that
   * stays legible across A4 landscape.
   */
  pdf?: boolean;
  get: (t: any) => any;
}

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// Credits add, debits subtract — the same convention as the table's subtotal,
// so the exported total ties out against what was on screen.
const signedAmount = (t: any) =>
  (t.transaction_type === 'CREDIT' ? 1 : -1) * (parseFloat(t.amount) || 0);

const fmtAmount = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Every field an entry carries, in reading order. Proof is deliberately absent:
// it's a link to a file, which is meaningless in a printed table and is the one
// column the client asked to leave out.
//
// `activeSlots` is the server's list of Subledger levels that exist at all, so
// this already is every subledger column — there are no others to miss.
const buildColumns = (activeSlots: number[]): Col[] => [
  { label: 'Date',        width: 14, weight: 15, pdf: true, get: t => fmtDate(t.transaction_date) },
  { label: 'Type',        width: 10, weight: 10, get: t => t.transaction_type || '' },
  { label: 'Ledger',      width: 20, weight: 17, pdf: true, get: t => t.ledger_category_name || '' },
  { label: 'Subledger 1', width: 18, weight: 16, pdf: true, get: t => t.ledger_subcategory_name || '' },
  ...activeSlots.map(slot => ({
    label: `Subledger ${slot}`, width: 18, weight: 16, pdf: true,
    get: (t: any) => t[`ledger_subledger_${slot}_name`] || '',
  })),
  // Particulars is hidden from the ledger (see LedgerWorkspace.tsx) so it is
  // left out of its exports too. Commented rather than deleted: the column and
  // every stored value still exist, and restoring it is un-commenting this line.
  // { label: 'Particulars',      width: 30, weight: 24, pdf: true, get: t => t.particular || '' },
  { label: 'Remarks',          width: 26, weight: 20, pdf: true, get: t => t.remarks || '' },
  { label: 'Mode',             width: 14, weight: 12, pdf: true, get: t => t.payment_mode || '' },
  { label: 'Transferred From', width: 18, weight: 14, get: t => t.transferred_from_name || '' },
  { label: 'Sender',           width: 18, weight: 14, get: t => t.sender_name || '' },
  { label: 'Receiver',         width: 18, weight: 14, get: t => t.receiver_name || '' },
  { label: 'Entered By',       width: 22, weight: 16, get: t => t.entered_by_email || '' },
  // Otherwise this only exists on screen as the blue row tint, which no export
  // can carry.
  { label: 'Paid From',        width: 16, weight: 15, pdf: true, get: t => (t.paid_from_petty_cash ? 'Petty cash' : 'Non-petty cash') },
  { label: 'Amount',           width: 15, weight: 15, pdf: true, numeric: true, get: (t: any) => signedAmount(t) },
];

const safeName = (s: string) => s.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');

// ── Excel ───────────────────────────────────────────────────────────────────

// Row 1 and column A are left empty as a margin, so nothing sits jammed against
// the edge of the sheet. Everything below is written relative to these.
const FIRST_ROW = 2;
const FIRST_COL = 2;   // column B

export async function downloadLedgerExcel(o: LedgerExportOptions): Promise<void> {
  const cols = buildColumns(o.activeSlots);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Ledger');

  ws.getColumn(1).width = 3;                 // the empty margin column
  ws.getColumn(FIRST_COL).width = 8;         // SL No.
  cols.forEach((c, i) => { ws.getColumn(FIRST_COL + 1 + i).width = c.width; });
  const lastCol = FIRST_COL + cols.length;

  let r = FIRST_ROW;
  const banner = (value: string, font: Partial<ExcelJS.Font>) => {
    ws.getCell(r, FIRST_COL).value = value;
    ws.mergeCells(r, FIRST_COL, r, lastCol);
    ws.getCell(r, FIRST_COL).font = font;
    ws.getCell(r, FIRST_COL).alignment = { horizontal: 'left' };
    r++;
  };

  // The ledger path is the subject of the sheet, so it *is* the title — there's
  // no separate document name, which only ever restated the screen it came from.
  banner(o.ledgerPath, { bold: true, size: 14, color: { argb: 'FF0F172A' } });
  banner(o.periodLabel, { size: 10, color: { argb: 'FF64748B' } });
  r++;   // blank line between the heading and the filter block

  const pairs: [string, string][] = [...o.summary, ['Entries', String(o.rows.length)]];
  pairs.forEach(([label, value]) => {
    ws.getCell(r, FIRST_COL).value = label;
    ws.getCell(r, FIRST_COL).font = { size: 10, bold: true, color: { argb: 'FF64748B' } };
    ws.getCell(r, FIRST_COL + 1).value = value;
    ws.mergeCells(r, FIRST_COL + 1, r, lastCol);
    ws.getCell(r, FIRST_COL + 1).font = { size: 10, color: { argb: 'FF334155' } };
    ws.getCell(r, FIRST_COL + 1).alignment = { horizontal: 'left' };
    r++;
  });

  r++;   // blank line before the table
  const headerRowIdx = r;
  const headerRow = ws.getRow(headerRowIdx);
  ['SL No.', ...cols.map(c => c.label)].forEach((label, i) => {
    const cell = headerRow.getCell(FIRST_COL + i);
    cell.value = label;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
  });
  r++;

  o.rows.forEach((t, i) => {
    const row = ws.getRow(r);
    // Carries the entry's stored ledger_no so the sheet and the screen call the
    // same entry by the same number; falls back to position for older rows.
    [t.ledger_no ?? i + 1, ...cols.map(c => c.get(t))].forEach((value, ci) => {
      const cell = row.getCell(FIRST_COL + ci);
      cell.value = value;
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      const col = cols[ci - 1];
      if (col?.numeric) {
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right' };
      }
      if (i % 2 === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
      }
    });
    r++;
  });

  const totalRow = ws.getRow(r);
  totalRow.getCell(FIRST_COL).value = 'Total';
  totalRow.getCell(lastCol).value = o.netTotal;
  totalRow.getCell(lastCol).numFmt = '#,##0.00';
  for (let c = FIRST_COL; c <= lastCol; c++) {
    totalRow.getCell(c).font = { bold: true };
    totalRow.getCell(c).border = { top: { style: 'thin', color: { argb: 'FF94A3B8' } } };
  }

  ws.views = [{ state: 'frozen', ySplit: headerRowIdx }];
  ws.autoFilter = {
    from: { row: headerRowIdx, column: FIRST_COL },
    to: { row: headerRowIdx, column: lastCol },
  };

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf]), `${safeName(o.fileBase)}.xlsx`);
}

// ── PDF ─────────────────────────────────────────────────────────────────────

const PAGE_W = 297;   // A4 landscape, millimetres
const PAGE_H = 210;
const MARGIN = 10;
const USABLE = PAGE_W - MARGIN * 2;
const SL_W = 12;      // the SL No. column; the rest share what's left
const ROW_H = 5;

// jsPDF's built-in fonts are WinAnsi-encoded and have no ₹ (U+20B9) glyph — it
// would come out as a blank or a stray character. The other PDFs in this app
// dodge that by rasterising a DOM node; this one draws real text, so it says
// "Rs." instead.
const RUPEE = 'Rs.';

export function downloadLedgerPdf(o: LedgerExportOptions): void {
  // The printable subset — see Col.pdf. The Excel gets the full set.
  const cols = buildColumns(o.activeSlots).filter(c => c.pdf);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const totalWeight = cols.reduce((s, c) => s + c.weight, 0);
  const widths = cols.map(c => (c.weight / totalWeight) * (USABLE - SL_W));

  const fit = (text: string, width: number) => {
    if (!text) return '';
    if (doc.getTextWidth(text) <= width) return text;
    let out = text;
    while (out.length > 1 && doc.getTextWidth(out + '…') > width) out = out.slice(0, -1);
    return out + '…';
  };

  let y = 0;

  const drawTableHeader = () => {
    doc.setFillColor(37, 99, 235);
    doc.rect(MARGIN, y, USABLE, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    let x = MARGIN;
    doc.text('SL', x + 1.5, y + 4.7);
    x += SL_W;
    cols.forEach((c, i) => {
      const label = c.label === 'Amount' ? `Amount (${RUPEE})` : c.label;
      if (c.numeric) doc.text(fit(label, widths[i] - 3), x + widths[i] - 1.5, y + 4.7, { align: 'right' });
      else doc.text(fit(label, widths[i] - 3), x + 1.5, y + 4.7);
      x += widths[i];
    });
    y += 7;
  };

  const newPage = (first = false) => {
    if (!first) doc.addPage();
    y = MARGIN;

    // The ledger path IS the title — there's no separate document name, which
    // only ever restated the screen it came from.
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(fit(o.ledgerPath, USABLE), MARGIN, y + 4);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(o.periodLabel, MARGIN, y + 3.5);
    y += 6;

    // The remaining filters, so a printed page still explains itself. Two per
    // line to keep the block short.
    doc.setFontSize(7.5);
    const pairs: [string, string][] = [...o.summary, ['Entries', String(o.rows.length)]];
    const colW = USABLE / 2;
    pairs.forEach(([label, value], i) => {
      const x = MARGIN + (i % 2) * colW;
      if (i % 2 === 0 && i > 0) y += 4;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(`${label}:`, x, y + 3.2);
      const labelW = doc.getTextWidth(`${label}:`) + 2;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      doc.text(fit(value, colW - labelW - 4), x + labelW, y + 3.2);
    });
    y += 7;

    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 3;

    drawTableHeader();
  };

  newPage(true);

  doc.setFont('helvetica', 'normal');
  o.rows.forEach((t, i) => {
    if (y + ROW_H > PAGE_H - MARGIN - 6) {
      newPage();
      doc.setFont('helvetica', 'normal');
    }
    if (i % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(MARGIN, y, USABLE, ROW_H, 'F');
    }
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);
    let x = MARGIN;
    doc.text(String(t.ledger_no ?? i + 1), x + 1.5, y + 3.4);
    x += SL_W;
    cols.forEach((c, ci) => {
      const raw = c.get(t);
      if (c.numeric) {
        const n = Number(raw) || 0;
        // Debits are the norm here, so red-on-negative would paint the page.
        // Only the sign distinguishes them, as in the table.
        doc.text(fmtAmount(n), x + widths[ci] - 1.5, y + 3.4, { align: 'right' });
      } else {
        doc.text(fit(String(raw ?? ''), widths[ci] - 3), x + 1.5, y + 3.4);
      }
      x += widths[ci];
    });
    doc.setDrawColor(241, 245, 249);
    doc.line(MARGIN, y + ROW_H, PAGE_W - MARGIN, y + ROW_H);
    y += ROW_H;
  });

  // Total, on a fresh page if the last one has no room left for it.
  if (y + ROW_H > PAGE_H - MARGIN - 6) newPage();
  doc.setDrawColor(148, 163, 184);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text('Total', MARGIN + 1.5, y + 4);
  doc.text(
    `${RUPEE} ${fmtAmount(o.netTotal)}`,
    PAGE_W - MARGIN - 1.5, y + 4, { align: 'right' }
  );

  // Page numbers last, once the total page count is known.
  const pages = doc.getNumberOfPages();
  const stamp = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Generated ${stamp}`, MARGIN, PAGE_H - 5);
    doc.text(`Page ${p} of ${pages}`, PAGE_W - MARGIN, PAGE_H - 5, { align: 'right' });
  }

  doc.save(`${safeName(o.fileBase)}.pdf`);
}
